import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Tag as TagIcon, X } from 'lucide-react';
import { api } from '../utils/api';

// Shared tag UI. Tags are org-scoped labels pinned on brands; the same
// chips render on the Brands grid, brand detail, Rankings rows and the
// dashboard analytics lists so a tag reads identically everywhere.

// A tag's hex color is used as a tinted background + solid text, which
// stays legible in both light and dark themes.
function chipColors(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return { bg: 'rgba(99,102,241,0.14)', fg: '#6366f1' };
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return { bg: `rgba(${r},${g},${b},0.14)`, fg: `#${m[1]}` };
}

export function TagChip({ tag, onRemove, small = false }) {
  const { bg, fg } = chipColors(tag.color);
  // An automatic tag's membership is recomputed, so there is nothing to
  // unpin — the ✕ would 409 and, if it didn't, the brand would rejoin at
  // the next sync. The title says why instead.
  const removable = onRemove && !tag.is_auto;
  return (
    <span
      title={tag.is_auto ? `"${tag.name}" is applied automatically` : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        background: bg,
        color: fg,
        fontWeight: 600,
        fontSize: small ? '0.68rem' : '0.75rem',
        padding: small ? '0.1rem 0.45rem' : '0.15rem 0.55rem',
        borderRadius: '9999px',
        whiteSpace: 'nowrap',
      }}
    >
      {tag.name}
      {removable && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(tag); }}
          title={`Remove tag "${tag.name}" from this brand`}
          aria-label={`Remove tag ${tag.name}`}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'inherit', display: 'inline-flex', padding: 0,
          }}
        >
          <X size={small ? 10 : 12} />
        </button>
      )}
    </span>
  );
}

// Read-only chip row (Rankings, Dashboard, anywhere without editing).
export function TagChipList({ tags, small = true }) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: '0.3rem', flexWrap: 'wrap' }}>
      {tags.map((t) => <TagChip key={t.id} tag={t} small={small} />)}
    </span>
  );
}

// Dropdown filter shared by Rankings and Brands. `tags` comes from
// api.listTags() so the option list always matches the org catalog.
// Rendered even when the catalog is empty so the feature is
// discoverable — the placeholder option says where tags come from.
export function TagFilterSelect({ tags, value, onChange, width = '150px' }) {
  const empty = !tags || tags.length === 0;
  return (
    <div className="filter-group">
      <TagIcon size={16} />
      <select
        className="form-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width }}
        title={empty
          ? 'No tags yet — add one to a brand with the "+ Tag" button on the Brands page'
          : 'Filter by tag'}
      >
        <option value="">All Tags</option>
        {empty && (
          <option value="" disabled>No tags yet — tag brands on the Brands page</option>
        )}
        {(tags || []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}{typeof t.brand_count === 'number' ? ` (${t.brand_count})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

// Editable chip row for one brand: existing chips get a remove ✕, and
// the trailing "+" opens a picker listing the org's tags with a
// type-to-create input, so defining a new tag never leaves the page.
// Calls onBrandUpdated(updatedBrand) after every successful change.
export function BrandTagEditor({ brand, onBrandUpdated, small = true }) {
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState(null);   // null = not loaded yet
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openPicker = async () => {
    setOpen(true);
    setError(null);
    try {
      const data = await api.listTags();
      setAllTags(data.tags || []);
    } catch (err) {
      setError(err.message);
      setAllTags([]);
    }
  };

  const mutate = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      const resp = await fn();
      if (resp.brand && onBrandUpdated) onBrandUpdated(resp.brand);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const assign = async (tag) => {
    const ok = await mutate(() => api.addBrandTag(brand.id, { tag_id: tag.id }));
    if (ok) { setOpen(false); setQuery(''); }
  };

  const createAndAssign = async () => {
    const name = query.trim();
    if (!name) return;
    const ok = await mutate(() => api.addBrandTag(brand.id, { name }));
    if (ok) { setOpen(false); setQuery(''); }
  };

  const remove = (tag) => mutate(() => api.removeBrandTag(brand.id, tag.id));

  const assignedIds = new Set((brand.tags || []).map((t) => t.id));
  // Automatic tags are offered nowhere to pin by hand: brands join and
  // leave them on their own, so the assign call is refused. They still
  // show as chips on the brands that carry them, and in the tag filters.
  const candidates = (allTags || [])
    .filter((t) => !t.is_auto)
    .filter((t) => !assignedIds.has(t.id))
    .filter((t) => !query.trim() || t.name.toLowerCase().includes(query.trim().toLowerCase()));
  const exactExists = (allTags || []).some(
    (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
  );

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {(brand.tags || []).map((t) => (
        <TagChip key={t.id} tag={t} small={small} onRemove={busy ? null : remove} />
      ))}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPicker())}
        title="Add a tag to this brand"
        aria-label={`Add a tag to ${brand.name}`}
        disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
          background: 'none', cursor: 'pointer',
          border: '1px dashed var(--border, #d1d5db)',
          color: 'var(--text-secondary)',
          fontSize: small ? '0.68rem' : '0.75rem',
          padding: small ? '0.1rem 0.45rem' : '0.15rem 0.55rem',
          borderRadius: '9999px',
        }}
      >
        {busy ? <Loader2 size={10} className="spin" /> : <Plus size={10} />} Tag
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
            minWidth: '220px', maxWidth: '280px',
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '0.5rem',
          }}
        >
          <input
            type="text"
            className="form-control"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (candidates.length === 1) assign(candidates[0]);
                else if (query.trim() && !exactExists) createAndAssign();
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder="Search or create a tag…"
            style={{ width: '100%', fontSize: '0.8rem', marginBottom: '0.4rem' }}
          />

          {allTags === null && (
            <div style={{ padding: '0.4rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              <Loader2 size={12} className="spin" /> Loading tags…
            </div>
          )}

          <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {candidates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => assign(t)}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '0.25rem 0.3rem', borderRadius: '6px', textAlign: 'left',
                }}
              >
                <TagChip tag={t} small />
                {typeof t.brand_count === 'number' && (
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                    {t.brand_count}
                  </span>
                )}
              </button>
            ))}

            {allTags !== null && candidates.length === 0 && !query.trim() && (
              <div style={{ padding: '0.3rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                No more tags — type a name to create one.
              </div>
            )}

            {query.trim() && !exactExists && (
              <button
                type="button"
                onClick={createAndAssign}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.35rem',
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '0.25rem 0.3rem', borderRadius: '6px', textAlign: 'left',
                  fontSize: '0.78rem', color: 'var(--primary, #6366f1)', fontWeight: 600,
                }}
              >
                <Plus size={12} /> Create “{query.trim()}”
              </button>
            )}
          </div>

          {error && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#b91c1c' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
